import { observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { DbKey } from '../../../adl-gen/common/db';
import * as DB from '../../../adl-gen/totalinteraction/futurefit/db';
import { CompletionEvent } from '../../../adl-gen/totalinteraction/futurefit/db/learningevent';
import * as FE from '../../../adl-gen/totalinteraction/futurefit/frontend';
import * as R from '../../../adl-gen/totalinteraction/futurefit/requests';
import { withI18n, WithI18nProps } from '../../../internationalization/i18n-hoc';
import { i18next } from '../../../internationalization/i18next';
import { MaybeLoading, Spinner } from '../../../loader/loader';
import { Route } from '../../../routing/app-routes';
import { CloseIcon } from '../../common/icons';
import { PageLayoutFn } from '../../common/page-layout/page-layout';
import { DnDContainer, DraggableOption } from '../../components/drag-and-drop/drag-and-drop-container';
import { Button } from '../../widgets/button/button';
import { CheckItem } from '../../widgets/check-item/check-item';
import { MarkdownContent } from '../../widgets/markdown-content/markdown-content';
import { RadioItem } from '../../widgets/radio-item/radio-item';

const styles = require('./quiz-question-screen.css');

/** Props for the component */
export interface QuizQuestionScreenProps {
  /** Constructor for the page layout */
  pageLayout: PageLayoutFn;

  /** The quiz   */
  userQuizSummary: MaybeLoading<FE.UserQuizSummary>;

  /** The question to display */
  nextQuestion: MaybeLoading<R.NextQuestionResp>;

  /** Goto another page */
  navigateTo(route: Route): void;

  /** Heartbeat callback to indicate the page is still alive */
  onHeartbeat(): Promise<void>;

  /** Callback when an answer is selected but not yet submitted */
  onSelect(answer: DB.AnswerResponse): Promise<void>;

  /** Callback to answer the question and proceed */
  onNext(answer: R.QuizAnswerQuestionReq): Promise<void>;

  /** Callback to complete the quiz once done */
  onDone(): Promise<void>;

  /** Method to pull out a one-time tracked link for embedded offerings */
  getEmbeddedCourseInfo?(trainingOfferingId: DbKey<DB.TrainingOffering>, userId: DbKey<DB.AppUser>): Promise<R.TrackedLinkResp>;

  /** Callback to generate completion events for embedded offerings in a non-Production environment */
  onGenerateCompletionEvent?(courseInfo: FE.TrackedCourseInfo): Promise<void>;

  /** Callback to poll for completion events for embedded offerings */
  onFetchCompletionEvent?(trainingOfferingId: DbKey<DB.TrainingOffering>, userId: DbKey<DB.AppUser>): Promise<CompletionEvent | null>;
}

@observer
export class QuizQuestionScreen extends React.Component<QuizQuestionScreenProps> {
  render() {
    const { nextQuestion, userQuizSummary } = this.props;
    if (userQuizSummary.kind === 'error') {
      return <div className={styles.errorText}>{userQuizSummary.error}</div>;
    } else if (nextQuestion.kind === 'error') {
      return <div className={styles.errorText}>{nextQuestion.error}</div>;
    } else if (userQuizSummary.kind === 'value' && nextQuestion.kind === 'value') {
      return (
        <QuizQuestion
          pageLayout={this.props.pageLayout}
          userQuizSummary={userQuizSummary.value}
          /// TODO: cop out
          nextQuestion={nextQuestion.value.value as R.QuizNextQuestionResp}
          navigateTo={this.props.navigateTo}
          onSelect={this.props.onSelect}
          onHeartbeat={this.props.onHeartbeat}
          onNext={this.props.onNext}
          onDone={this.props.onDone}
          getEmbeddedCourseInfo={this.props.getEmbeddedCourseInfo}
          onGenerateCompletionEvent={this.props.onGenerateCompletionEvent}
          onFetchCompletionEvent={this.props.onFetchCompletionEvent}
        />
      );
    } else {
      return <Spinner />;
    }
  }
}

/** Props for the component */
export interface QuizQuestionProps extends WithI18nProps {
  /** Constructor for the page layout */
  pageLayout: PageLayoutFn;

  /** The quiz   */
  userQuizSummary: FE.UserQuizSummary;

  /** The question to display */
  nextQuestion: R.QuizNextQuestionResp;

  /** Goto another page */
  navigateTo(route: Route): void;

  /** Heartbeat callback to indicate the page is still alive */
  onHeartbeat(): Promise<void>;

  /** Callback when an answer is selected but not yet submitted */
  onSelect(answer: DB.AnswerResponse): Promise<void>;

  /** Callback to answer the question and proceed */
  onNext(answer: R.QuizAnswerQuestionReq): Promise<void>;

  /** Callback to complete the quiz once done */
  onDone(): Promise<void>;

  /** Method to pull out a one-time tracked link for embedded offerings */
  getEmbeddedCourseInfo?(trainingOfferingId: DbKey<DB.TrainingOffering>, userId: DbKey<DB.AppUser>): Promise<R.TrackedLinkResp>;

  /** Callback to generate completion events for embedded offerings in a non-Production environment */
  onGenerateCompletionEvent?(courseInfo: FE.TrackedCourseInfo): Promise<void>;

  /** Callback to poll for completion events for embedded offerings */
  onFetchCompletionEvent?(trainingOfferingId: DbKey<DB.TrainingOffering>, userId: DbKey<DB.AppUser>): Promise<CompletionEvent | null>;
}

/**
 * The Quiz Question Screen screen.
 */
@observer
class QuizQuestion0 extends React.Component<QuizQuestionProps> {
  @observable answerResponse: DB.AnswerResponse | null = null;
  @observable submittedResponses: boolean[] | null = null;
  @observable embeddedResponses: boolean[] | null = null;
  heartbeatIntervalId: number | null = null;
  embeddedInfo?: R.TrackedLinkResp;

  /// We have to set answerResponse for an introduction or conclusion question, as there
  /// is no update action available. We can't set this in the render phase.
  constructor(props: Readonly<QuizQuestionProps>) {
    super(props);

    /// Intros and Conclusions: Set up response array for 'I have read'/'I accept'-type responses.
    /// Only do this if these answers exist. If the 'slide' has no answers, simply set the field to 'submitted' and move on.
    const nextQuestion = props.nextQuestion;
    if (nextQuestion.kind === 'nextQuestion') {
      if (nextQuestion.value.answerType.kind === 'submitted') {
        if (nextQuestion.value.answers.length > 0) {
          runInAction(() => {
            this.submittedResponses = new Array<boolean>(nextQuestion.value.answers.length);
            this.submittedResponses.fill(false);
          });
        } else {
          /// Just set answerResponse
          runInAction(() => this.answerResponse = { kind: 'submitted' });
        }
      }
      if (nextQuestion.value.answerType.kind === 'embedded') {
        if (nextQuestion.value.answers.length > 0) {
          runInAction(() => {
            this.embeddedResponses = new Array<boolean>(nextQuestion.value.answers.length);
            this.embeddedResponses.fill(false);
          });
        } else {
          /// Just leave answerResponse empty
          runInAction(() => this.answerResponse = null);
        }
      }
      this.getEmbeddedCourseInfo();
    }
  }

  getEmbeddedCourseInfo = async () => {
    if (this.props.nextQuestion.kind === 'nextQuestion') {
      const { category } = this.props.nextQuestion.value;
      if (this.props.getEmbeddedCourseInfo
        && category.kind !== 'skill'
        && category.value.kind === 'embeddedOffering'
      ) {
        // If we're pulling in an embedded offering, get the src URL for it
        // TODO the one-time URL is actually generated on the server, we no longer use this to set it
        // TODO At the moment, this just gets the course info (by running the call again - so it will 
        // TODO be a different one-time link - but the event should have the same data - except for the
        // TODO one-time link the call should be idempotent)
        // TODO rename/change to just fetch course info/include the course info in getNextQuestion
        this.embeddedInfo = await this.props.getEmbeddedCourseInfo(
          category.value.value,
          this.props.userQuizSummary.userId
        );
      }
    }
  }

  componentDidMount() {
    const HEARTBEAT_PERIOD = 30000;
    this.heartbeatIntervalId = window.setInterval(this.onHeartbeat, HEARTBEAT_PERIOD);
  }

  /// FIXME: this is deprecated. Can use equivalent useLayoutEffect() hook if it's available. Otherwise use useEffect/useRef
  /** @deprecated */
  componentWillUnmount() {
    if (this.heartbeatIntervalId) {
      window.clearInterval(this.heartbeatIntervalId);
    }
  }

  onHeartbeat = async (): Promise<void> => {
    const { onHeartbeat, onFetchCompletionEvent, userQuizSummary, nextQuestion } = this.props;
    await onHeartbeat();
    if (onFetchCompletionEvent) {
      if (nextQuestion.kind === 'nextQuestion'
        && nextQuestion.value.category.kind !== 'skill'
        && nextQuestion.value.category.value.kind === 'embeddedOffering') {
        const trainingOfferingId = nextQuestion.value.category.value.value;
        runInAction(async () => {
          // Use this to query for events of type 'completion' with the email address, courseId, and (later maybe) trackId
          // If there's a historical completion event we should be able to assume the user has already done the 'course'
          const completionEvent = await onFetchCompletionEvent(trainingOfferingId, userQuizSummary.userId);
          if (completionEvent !== null)
            this.updateAnswerResponse({ kind: 'embedded' });
        });
      }
    }
  }

  /** Renders the component */
  render(): JSX.Element {
    return <this.props.pageLayout>{this.renderContent()}</this.props.pageLayout>;
  }

  renderContent(): JSX.Element {
    switch (this.props.nextQuestion.kind) {
      case 'completed':
        return this.renderCompleted();
      case 'nextQuestion':
        return this.renderQuestion(this.props.nextQuestion.value);
    }
  }

  renderCompleted = (): JSX.Element => {
    const { userQuizSummary } = this.props;
    const completedText = userQuizSummary.lockResults
      ? i18next.t('question.results-locked', "Thank you for completing this assessment. Your results will be available soon.")
      : i18next.t('question.completed', "You've completed this assessment. Continue to see your results.");
    return (
      <div className={styles.container}>
        <div className={styles.margin} />
        <div className={styles.questionContent}>
          <p>{completedText}</p>
          <br />
          <Button onClick={this.props.onDone} variant="round">
            {i18next.t('question.continue', 'Continue')}
          </Button>
        </div>
        <div className={styles.margin} />
      </div>
    );
  };

  returnToAssessmentsScreen(): void {
    return this.props.navigateTo({
      route: 'my-assessments'
    });
  }

  renderQuestion(question: FE.QuizNextQuestion): JSX.Element {
    const { t, userQuizSummary } = this.props;
    /// default
    /// For display *only*: If there's an intro and/or an outro slide, reduce the count
    const questionNumber = userQuizSummary.introduction ? question.questionNumber - 1 : question.questionNumber;
    let totalQuestions = userQuizSummary.introduction ? question.totalQuestions - 1 : question.totalQuestions;
    totalQuestions = userQuizSummary.conclusion ? totalQuestions - 1 : totalQuestions;

    const { answerType, category, content } = question;
    let title = i18next.t('question.heading', {
      questionNumber,
      totalQuestions,
      defaultValue: `${questionNumber} of ${totalQuestions}`
    });
    let finishText = i18next.t('question.finish', 'Finish');
    switch (answerType.kind) {
      case 'submitted':
        switch (category.kind) {
          case 'introduction':
            title = i18next.t('question.introduction', 'Introduction');
            break;
          case 'conclusion':
            title = i18next.t('question.conclusion', 'Conclusion');
            break;
          default: break;
        }
      case 'embedded':
        switch (category.kind) {
          case 'introduction':
            title = i18next.t('question.introduction', 'Introduction');
            break;
          case 'conclusion':
            title = i18next.t('question.summary', 'Your skillseeker Debrief');
            finishText = i18next.t('question.see-results', 'See Results');
            break;
          default: break;
        }

    }
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.exit}>
            <CloseIcon size={'xsmall'} onClick={async () => this.returnToAssessmentsScreen()} />
          </div>
          <div className={styles.title}>{title}</div>
          <div className={styles.question}>
            <MarkdownContent text={t(content)} />
          </div>
          {this.renderAnswers(question)}
          <div className={styles.buttons}>
            <Button
              // tslint:disable-next-line: no-async-without-await
              onClick={async () => this.returnToAssessmentsScreen()}
              variant={'tertiary'}
              className={styles.button}
            >
              {i18next.t('question.save-close', 'Save and Close')}
            </Button>
            <Button
              onClick={this.onNextQuestion}
              disabled={this.answerResponse === null}
              className={styles.button}
            >
              {question.questionNumber === question.totalQuestions
                ? finishText
                : i18next.t('question.next', 'Next')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  renderAnswers(question: FE.QuizNextQuestion): JSX.Element | JSX.Element[] | null {
    switch (question.answerType.kind) {
      case 'orderedResponse':
        return <div className={styles.answers}>{this.renderOrderedAnswers(question.answers)}</div>;
      default:
        return <div className={styles.answers}>{this.renderSelectionAnswers(question)}</div>;
    }
  }

  renderSelectionAnswers(question: FE.QuizNextQuestion): JSX.Element[] {
    return question.answers.map((ac, i) => this.renderAnswerContent(question, ac, i));
  }

  renderAnswerContent = (question: FE.QuizNextQuestion, answer: DB.I18nContent, i: number): JSX.Element => {
    const { t } = this.props;
    switch (question.answerType.kind) {
      case 'singleResponse': {
        const answerResponse =
          this.answerResponse && this.answerResponse.kind === 'singleResponse' ? this.answerResponse.value : null;
        return this.renderSingleAnswerContent(t(answer), answerResponse, i);
      }
      case 'multiResponse': {
        const answerResponse =
          this.answerResponse && this.answerResponse.kind === 'multiResponse' ? this.answerResponse.value : null;
        return this.renderMultiAnswerContent(t(answer), answerResponse, i);
      }
      case 'submitted': {
        const answerResponse = this.submittedResponses;
        return this.renderSubmittedAnswerContent(t(answer), answerResponse, i);
      }
      case 'embedded': {
        const answerResponse = this.embeddedResponses;
        return this.renderEmbeddedAnswerContent(t(answer), answerResponse, i);
      }
      default:
        return <div>Answer type not implemented</div>;
    }
  };

  renderSingleAnswerContent = (answerContent: string, answerResponse: number | null, i: number): JSX.Element => {
    return (
      <div key={i}>
        <RadioItem
          size={'xxsmall'}
          label={answerContent}
          selected={answerResponse !== null && answerResponse === i}
          onChange={_ => this.updateSingleAnswerResponse(i)}
        />
      </div>
    );
  };

  renderMultiAnswerContent = (answerContent: string, answerResponse: number[] | null, i: number): JSX.Element => {
    return (
      <div key={i}>
        <CheckItem
          size={'xxsmall'}
          label={answerContent}
          selected={new Set(answerResponse).has(i)}
          onChange={_ => this.updateMultiAnswerResponse(i)}
        />
      </div>
    );
  };

  renderSubmittedAnswerContent = (
    answerContent: string,
    submittedResponses: boolean[] | null,
    i: number
  ): JSX.Element => {
    return (
      <div key={i}>
        <CheckItem
          size={'xxsmall'}
          label={answerContent}
          selected={submittedResponses !== null && submittedResponses[i]}
          onChange={_ => this.updateSubmittedAnswerResponse(i)}
        />
      </div>
    );
  };

  renderEmbeddedAnswerContent = (
    answerContent: string,
    embeddedResponses: boolean[] | null,
    i: number
  ): JSX.Element => {
    return (
      <div key={i}>
        <CheckItem
          size={'xxsmall'}
          label={answerContent}
          selected={embeddedResponses !== null && embeddedResponses[i]}
          onChange={_ => this.updateEmbeddedAnswerResponse(i)}
        />
      </div>
    );
  };

  renderOrderedAnswers(answers: DB.I18nContent[]): JSX.Element {
    const { t } = this.props;
    // Default order on first render
    let draggableAnswers: DraggableOption[] = answers.map((ac, i) => {
      return {
        id: i,
        text: t(ac)
      };
    });
    if (this.answerResponse !== null && this.answerResponse.kind === 'orderedResponse') {
      const order: number[] = this.answerResponse.value;
      draggableAnswers = answers.map((ac, i) => {
        return {
          id: order[i],
          text: t(ac)
        };
      });
    }
    return (
      <DnDContainer
        draggableOptions={draggableAnswers}
        onMove={c =>
          this.updateAnswerResponse({
            kind: 'orderedResponse',
            value: c.map(item => item.id)
          })
        }
      />
    );
  }

  onNextQuestion = async (): Promise<void> => {
    if (this.answerResponse && this.props.nextQuestion.kind === 'nextQuestion') {
      await this.props.onNext({
        userQuizId: this.props.userQuizSummary.userQuizId,
        quizQuestionId: this.props.nextQuestion.value.quizQuestionId,
        answerResponse: this.answerResponse
      });
    }
  };

  async updateSingleAnswerResponse(selected: number) {
    await this.updateAnswerResponse({
      kind: 'singleResponse',
      value: selected
    });
  }

  async updateMultiAnswerResponse(toggled: number) {
    const selected: Set<number> =
      this.answerResponse && this.answerResponse.kind === 'multiResponse'
        ? new Set(this.answerResponse.value)
        : new Set();
    if (selected.has(toggled)) {
      selected.delete(toggled);
    } else {
      selected.add(toggled);
    }
    await this.updateAnswerResponse({
      kind: 'multiResponse',
      value: Array.from(selected)
    });
  }

  async updateSubmittedAnswerResponse(toggled: number) {
    if (this.submittedResponses !== null && this.answerResponse === null) {
      const selected: boolean = this.submittedResponses[toggled];
      runInAction(() => {
        if (this.submittedResponses !== null) {
          selected ? (this.submittedResponses[toggled] = false) : (this.submittedResponses[toggled] = true);
        }
      });
      /// If user has ticked every box, set to 'submitted' and enable 'Next' button. Once this is done,
      /// no further processing done
      if (this.submittedResponses.every(v => v)) {
        await this.updateAnswerResponse({ kind: 'submitted' });
      }
    }
  }

  async updateEmbeddedAnswerResponse(toggled: number) {
    const { nextQuestion, onGenerateCompletionEvent } = this.props;
    if (this.embeddedResponses !== null && this.answerResponse === null) {
      const selected: boolean = this.embeddedResponses[toggled];
      if (onGenerateCompletionEvent) {
        if (this.embeddedResponses !== null) {
          runInAction(() => {
            if (this.embeddedResponses !== null)
              selected ? (this.embeddedResponses[toggled] = false) : (this.embeddedResponses[toggled] = true);
          });
          // Generate a 'completion' event so that the heartbeat can pick it up
          // The hearbeat will call updateAnswerResponse
          if (this.embeddedResponses.every(v => v)) {
            if (nextQuestion.kind === 'nextQuestion'
              && this.embeddedInfo
              && this.embeddedInfo.course
            )
              await onGenerateCompletionEvent(this.embeddedInfo.course);  
          }
        }
      }
    }
  }

  updateAnswerResponse = async (answerResponse: DB.AnswerResponse) => {
    runInAction(() => {
      this.answerResponse = answerResponse;
    });
    await this.props.onSelect(answerResponse);
  };
}

export const QuizQuestion = withI18n(QuizQuestion0);


// WEBPACK FOOTER //
// ./src/ui/screens/quiz-question-screen/quiz-question-screen.tsx